/* eslint-disable max-len */
import { BlueprintInstanceType } from '../shared.enums';
import { PromotionalSku } from './promotional-sku.class';
import { addSkuCfn, totalAdjustmentCfn } from './functions';
import { KitchenDesignBase, KitchenDesignOptions } from '../kitchen-design-base';
import { PromotionalSkusDataIn, PromotionalSkusDataOut } from './types';

export class PromotionalSkus extends KitchenDesignBase<PromotionalSkusDataIn> {
	readonly children: Array<PromotionalSku>;
	/**
	 * The constructor of the 'PanelItems' class
	 * @param options provides context, also used to hydrate the instance
	*/
	constructor (options: KitchenDesignOptions<PromotionalSkusDataIn> = {}) {
		super(BlueprintInstanceType.PromotionalSkus, options);

		const { dataIn: data = null } = options;

		this.children = [];
		if (data && data.children?.length > 0) {
			data.children?.forEach((n) => this.children.push(new PromotionalSku({ dataIn: n, parent: this })));
		}
		this.sortChildren()

		this.setInitialized();
	}

	/**
	 * Get all children
	 *
	 */
	get totalSkus (): number { return this.children.length }

	public sortChildren (): void {
		this.children.sort((a, b) => {
			// Discounts first (before shipping and promotions)
			if (a.isDiscount && !b.isDiscount) return -1;
			if (!a.isDiscount && b.isDiscount) return 1;

			// Final-type SKUs last (after all discounts and promotions)
			if (a.isFinal && !b.isFinal) return 1;
			if (!a.isFinal && b.isFinal) return -1;

			const isAShipping = a.sku === 'SHIPPING' || a.sku === 'FREE-SHIPPING';
			const isBShipping = b.sku === 'SHIPPING' || b.sku === 'FREE-SHIPPING';

			if (isAShipping && !isBShipping) return -1;
			if (!isAShipping && isBShipping) return 1;

			if (isAShipping && isBShipping) {
				if (a.sku === 'SHIPPING' && b.sku === 'FREE-SHIPPING') return -1;
				if (a.sku === 'FREE-SHIPPING' && b.sku === 'SHIPPING') return 1;
				return 0;
			}

			const isAPercentage = a.adjustmentType === 'PERCENTAGE';
			const isBPercentage = b.adjustmentType === 'PERCENTAGE';

			if (!isAPercentage && isBPercentage) return -1;
			if (isAPercentage && !isBPercentage) return 1;

			return a.priceAdjustment - b.priceAdjustment;
		});
	}

	public discounts (subtotal: number): number {
		let total = subtotal;
		this.children.forEach((p) => {
			total += totalAdjustmentCfn(subtotal, p)
		})
		return total
	}
	get isValid (): boolean { return this.children.every((promotionalSku) => promotionalSku.isValid) }

	public validate (): void {
		this.children.forEach((promotionalSku) => promotionalSku.validate());
	}
	/**
	 * Creates and adds a new promotional sku and push the promotional-sku items in children
	 *
	 * @param sku sku of promotional-sku item to add
	 * @returns Instance of promotionalSku
	 */
	public newSku (sku: string): PromotionalSku {
		const pSku = addSkuCfn(this, sku);
		// console.log('pSku', pSku);
		if (pSku) this.children.push(pSku);
		return pSku;
	}

	/**
	 *
	 * @param id id of promotional-sku item to find
	 * @returns PromotionalSku
	*/
	public findById (id: string): PromotionalSku {
		return this.children.find((pi) => pi.id === id);
	}

	/**
	 *
	 * @param sku sku code of promotional-sku item to find
	 * @returns PromotionalSku
	*/
	public findBySku (sku: string): PromotionalSku {
		return this.children.find((ps) => ps.sku === sku);
	}

	// serialize
	public serialize (): PromotionalSkusDataOut {
		return {
			children: this.children.map((child) => child.serialize()),
			_metadata: {
				totalSkus: this.totalSkus
			}
		};
	}

	// public static promotionalSkus = Object.keys(promotionalSkuAssets);
}
